// Reports list. Flat tabular: date · title · call-type · score pill.
// Filters live behind a toggle and are multi-select for call type:
// each chip toggles individual membership in the active set; an empty set
// means "all types". Source is single-select (3 options, doesn't benefit
// from multi).
//
// Library deep-links from the sidebar set a `?preset=` URL param. The
// preset shadows the multi-select while it's active; clearing the preset
// drops back to plain multi-select.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  CALL_TYPE_LABELS,
  listAnalyses,
  SessionExpiredError,
  type AnalysisListItem,
  type CallType,
  type ReadingSource,
} from "../lib/api";
import { Crumbs } from "../components/Crumbs";
import { PageTitle } from "../components/PageTitle";
import { EarlierRow } from "../components/EarlierRow";

const CALL_TYPE_KEYS = Object.keys(CALL_TYPE_LABELS) as CallType[];

const SOURCE_FILTERS: ReadonlyArray<{ value: ReadingSource | "all"; label: string }> = [
  { value: "all", label: "All sources" },
  { value: "paste", label: "Paste" },
  { value: "granola", label: "Granola" },
  { value: "fathom", label: "Fathom" },
];

const PRESET_LABELS: Record<string, { label: string; types: ReadonlyArray<CallType> }> = {
  "senior-1to1": {
    label: "Senior 1:1",
    types: ["one_on_one_skip_level", "one_on_one_career_or_review"],
  },
  pitch: {
    label: "Pitch / board",
    types: ["leadership_internal", "leadership_investor"],
  },
  standup: { label: "Standup", types: ["team_standup"] },
  hard: { label: "Hard conversation", types: ["one_on_one_difficult_feedback"] },
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; items: AnalysisListItem[]; nextCursor: number | null }
  | { kind: "error"; message: string };

export function Reports() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const presetKey = searchParams.get("preset");
  const preset = presetKey ? PRESET_LABELS[presetKey] : null;
  const [callTypeFilter, setCallTypeFilter] = useState<ReadonlySet<CallType>>(() => new Set());
  const [sourceFilter, setSourceFilter] = useState<ReadingSource | "all">("all");
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);

  // Active call-types feeding the API + client-filter. Preset shadows the
  // user-selected set when it's set.
  const activeTypes = useMemo<ReadonlyArray<CallType>>(() => {
    if (preset) return preset.types;
    return Array.from(callTypeFilter);
  }, [preset, callTypeFilter]);

  const isMultiType = activeTypes.length > 1;

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      try {
        const args: Parameters<typeof listAnalyses>[0] = { limit: isMultiType ? 100 : 25 };
        if (activeTypes.length === 1) args.callType = activeTypes[0];
        if (sourceFilter !== "all") args.source = sourceFilter;
        const res = await listAnalyses(args);
        if (cancelled) return;
        // The API takes a single callType today; for multi-type filters we
        // fetch a wider window and filter client-side. Pagination is disabled
        // in that mode (cursor would lose other-type rows in the next page).
        const filtered = isMultiType
          ? res.items.filter((it) => activeTypes.includes(it.callType))
          : res.items;
        setState({
          kind: "ok",
          items: filtered,
          nextCursor: isMultiType ? null : res.nextCursor,
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof SessionExpiredError) {
          navigate("/login", { replace: true });
          return;
        }
        setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTypes, isMultiType, sourceFilter, navigate]);

  async function loadMore() {
    if (state.kind !== "ok" || state.nextCursor == null) return;
    setLoadingMore(true);
    try {
      const args: Parameters<typeof listAnalyses>[0] = {
        limit: 25,
        before: state.nextCursor,
      };
      if (activeTypes.length === 1) args.callType = activeTypes[0];
      if (sourceFilter !== "all") args.source = sourceFilter;
      const res = await listAnalyses(args);
      setState({
        kind: "ok",
        items: [...state.items, ...res.items],
        nextCursor: res.nextCursor,
      });
    } catch (err) {
      if (err instanceof SessionExpiredError) {
        navigate("/login", { replace: true });
        return;
      }
    } finally {
      setLoadingMore(false);
    }
  }

  function toggleCallType(type: CallType) {
    setCallTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function clearCallType(type: CallType) {
    setCallTypeFilter((prev) => {
      const next = new Set(prev);
      next.delete(type);
      return next;
    });
  }

  function clearAllCallTypes() {
    setCallTypeFilter(new Set());
  }

  const anyFilterApplied = preset != null || callTypeFilter.size > 0 || sourceFilter !== "all";

  return (
    <>
      <Crumbs trail={["Reports"]} />
      <PageTitle lede="Every reading you've taken. Open one for the full breakdown.">
        Reports.
      </PageTitle>

      <FilterRow
        preset={preset}
        clearPreset={() => {
          const next = new URLSearchParams(searchParams);
          next.delete("preset");
          setSearchParams(next, { replace: true });
        }}
        callTypeFilter={callTypeFilter}
        toggleCallType={toggleCallType}
        clearCallType={clearCallType}
        clearAllCallTypes={clearAllCallTypes}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
      />

      {state.kind === "loading" ? <ListSkeleton /> : null}

      {state.kind === "error" ? (
        <p style={{ color: "rgb(var(--color-warning))" }}>{state.message}</p>
      ) : null}

      {state.kind === "ok" && state.items.length === 0 ? (
        <EmptyReports filterApplied={anyFilterApplied} />
      ) : null}

      {state.kind === "ok" && state.items.length > 0 ? (
        <>
          <div>
            {state.items.map((a) => (
              <EarlierRow
                key={a.id}
                to={`/reports/${a.id}`}
                date={formatRowDate(a.meetingDate ?? a.createdAt)}
                title={a.meetingTitle?.trim() || humaniseCallType(a.callType)}
                ctx={humaniseCallType(a.callType)}
                score={typeof a.overallScore === "number" ? a.overallScore : undefined}
              />
            ))}
          </div>
          {state.nextCursor != null ? (
            <div style={{ marginTop: 24, textAlign: "center" }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void loadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function FilterRow({
  preset,
  clearPreset,
  callTypeFilter,
  toggleCallType,
  clearCallType,
  clearAllCallTypes,
  sourceFilter,
  setSourceFilter,
}: {
  preset: { label: string; types: ReadonlyArray<CallType> } | null;
  clearPreset: () => void;
  callTypeFilter: ReadonlySet<CallType>;
  toggleCallType: (t: CallType) => void;
  clearCallType: (t: CallType) => void;
  clearAllCallTypes: () => void;
  sourceFilter: ReadingSource | "all";
  setSourceFilter: (v: ReadingSource | "all") => void;
}) {
  const [open, setOpen] = useState(false);
  const activeSource = sourceFilter !== "all";
  const selectedTypes = useMemo(() => Array.from(callTypeFilter), [callTypeFilter]);
  const activeCount = (preset ? 1 : 0) + selectedTypes.length + (activeSource ? 1 : 0);
  const sourceLabel = activeSource
    ? SOURCE_FILTERS.find((f) => f.value === sourceFilter)?.label
    : null;
  // When a lot of types are selected, collapse them into a single counter
  // chip so the row doesn't grow into a wall of pills.
  const COLLAPSE_AT = 4;
  const showSelectedAsList = !preset && selectedTypes.length > 0 && selectedTypes.length < COLLAPSE_AT;
  const showSelectedAsCount = !preset && selectedTypes.length >= COLLAPSE_AT;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: open ? 12 : 0 }}>
        <button
          type="button"
          className={open ? "chip active" : "chip"}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="reports-filter-rows"
        >
          {open ? "Hide filters" : "Filter"}
          {activeCount > 0 ? <span style={{ marginLeft: 6, opacity: 0.75 }}>· {activeCount}</span> : null}
        </button>

        {preset ? (
          <button
            type="button"
            className="chip active"
            onClick={clearPreset}
            aria-label={`Clear ${preset.label} preset`}
          >
            {preset.label} ✕
          </button>
        ) : null}

        {showSelectedAsList
          ? selectedTypes.map((t) => (
              <button
                key={t}
                type="button"
                className="chip active"
                onClick={() => clearCallType(t)}
                aria-label={`Clear ${CALL_TYPE_LABELS[t]} filter`}
              >
                {CALL_TYPE_LABELS[t]} ✕
              </button>
            ))
          : null}

        {showSelectedAsCount ? (
          <button
            type="button"
            className="chip active"
            onClick={clearAllCallTypes}
            aria-label="Clear all type filters"
          >
            {selectedTypes.length} types ✕
          </button>
        ) : null}

        {sourceLabel ? (
          <button
            type="button"
            className="chip active"
            onClick={() => setSourceFilter("all")}
            aria-label={`Clear ${sourceLabel} source filter`}
          >
            {sourceLabel} ✕
          </button>
        ) : null}
      </div>

      {open ? (
        <div id="reports-filter-rows">
          {!preset ? (
            <>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "4px 0 8px" }}>
                <p className="frame-label" style={{ margin: 0 }}>By type</p>
                {selectedTypes.length > 0 ? (
                  <button
                    type="button"
                    onClick={clearAllCallTypes}
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10.5,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: "rgb(var(--color-ink-muted))",
                      background: "transparent",
                      border: 0,
                      cursor: "pointer",
                      padding: 0,
                    }}
                    aria-label="Clear all type filters"
                  >
                    Clear · {selectedTypes.length}
                  </button>
                ) : null}
              </div>
              <div className="chip-row" style={{ marginBottom: 12 }}>
                {CALL_TYPE_KEYS.map((value) => {
                  const selected = callTypeFilter.has(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      className={selected ? "chip active" : "chip"}
                      onClick={() => toggleCallType(value)}
                      aria-pressed={selected}
                    >
                      {CALL_TYPE_LABELS[value]}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}
          <p className="frame-label" style={{ margin: "4px 0 8px" }}>By source</p>
          <div className="chip-row">
            {SOURCE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                className={f.value === sourceFilter ? "chip active" : "chip"}
                onClick={() => setSourceFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EmptyReports({ filterApplied }: { filterApplied: boolean }) {
  if (filterApplied) {
    return (
      <p style={{ color: "rgb(var(--color-ink-muted))" }}>
        No readings match these filters. Try widening your selection.
      </p>
    );
  }
  return (
    <div style={{ padding: "32px 0", textAlign: "center" }}>
      <p style={{ color: "rgb(var(--color-ink-soft))", marginBottom: 18, fontSize: 15 }}>
        No readings yet. Connect a notetaker on Settings, then click Sync now on the Dashboard.
      </p>
      <Link to="/settings" className="btn btn-primary">
        Connect a notetaker
      </Link>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div style={{ opacity: 0.5 }} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="row" style={{ pointerEvents: "none" }}>
          <span
            className="when"
            style={{ background: "rgb(var(--color-surface-strong))", color: "transparent", borderRadius: 4 }}
          >
            00 MAY
          </span>
          <span className="what">
            <span
              className="title"
              style={{
                background: "rgb(var(--color-surface-strong))",
                color: "transparent",
                borderRadius: 4,
                display: "inline-block",
                width: "60%",
              }}
            >
              Loading
            </span>
          </span>
          <span className="arr">→</span>
        </div>
      ))}
    </div>
  );
}

function formatRowDate(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function humaniseCallType(ct: string): string {
  return ct
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}
